<template>
  <el-dialog v-model="visible" :title="t('admin.password.title')" width="520px" :close-on-click-modal="false" destroy-on-close>
    <el-form ref="formRef" :model="form" :rules="rules" :validate-on-rule-change="false" label-width="100px" @submit.prevent="submit">
      <el-form-item :label="t('admin.password.currentUser')"><span>{{ displayName }}</span></el-form-item>
      <el-form-item :label="t('admin.password.current')" prop="password"><el-input v-model="form.password" type="password" show-password autocomplete="current-password" /></el-form-item>
      <el-form-item :label="t('admin.password.next')" prop="newPassword"><el-input v-model="form.newPassword" type="password" show-password autocomplete="new-password" /></el-form-item>
      <el-form-item :label="t('admin.password.confirm')" prop="confirmPassword"><el-input v-model="form.confirmPassword" type="password" show-password autocomplete="new-password" /></el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="visible = false">{{ t('admin.password.cancel') }}</el-button>
      <el-button type="primary" :loading="saving" @click="submit">{{ t('admin.password.submit') }}</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { useI18n } from 'vue-i18n'

import { changeAdminPassword } from '@/api/admin/auth'

defineProps<{ displayName: string }>()
const emit = defineEmits<{ changed: [] }>()
const visible = ref(false)
const saving = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({ password: '', newPassword: '', confirmPassword: '' })
const { t } = useI18n()
const rules = computed<FormRules>(() => ({
  password: [{ required: true, message: t('admin.password.currentRequired'), trigger: 'blur' }],
  newPassword: [{ required: true, message: t('admin.password.nextRequired'), trigger: 'blur' }],
  confirmPassword: [
    { required: true, message: t('admin.password.confirmRequired'), trigger: 'blur' },
    { validator: (_rule, value, callback) => value === form.newPassword ? callback() : callback(new Error(t('admin.password.mismatch'))), trigger: 'blur' }
  ]
}))

const open = (): void => {
  Object.assign(form, { password: '', newPassword: '', confirmPassword: '' })
  visible.value = true
}
const submit = async (): Promise<void> => {
  if (!await formRef.value?.validate().catch(() => false)) return
  saving.value = true
  try {
    await changeAdminPassword({ password: form.password, newPassword: form.newPassword })
    visible.value = false
    ElMessage.success(t('admin.password.success'))
    emit('changed')
  } catch { ElMessage.error(t('admin.password.failed')) } finally { saving.value = false }
}

defineExpose({ open })
</script>
